// The Archived list on the web's Home (Josh 10-18 — docs/flows/handoff.yaml › archived-list: "just make a whole button
// and list of archived things I can just clear all or individual"; "I don't want to be guessing"). A Mac whose Claude
// Code extension has archived sessions (its `home` doc's `archived`, archivelist.js on the Mac) shows one button at the
// bottom of its lists — "Archived · 3" (none at 0). It opens a sheet: at the top Clear all with the total ("Clear all · 3
// sessions · 412 MB"), then one row per session saying plainly what it is — its title and the Mac's lines (what it was
// about, started → archived and its prompts, what it led to, its folder, its size broken down, its threads) — with
// Open, Reactivate and Delete, words on every button. Reactivate, Delete and Clear all ask first with the Mac's words
// (exactly what goes, with sizes). A row deleted goes from the sheet at once, so the next can be cleared straight
// after (no wait for the Mac's new list); a refusal brings it back with the Mac's why.
//
// Seams: home.js (`archivedButton` in a Mac's lists, `archivedSheet` over Home, `archivedTap` for its taps).
// tools/tests/web-ext-home.mjs.
import { esc, sheet, alert } from '../../core/kit.js'

/// The `archived` of a Mac's extensions' Home docs (app.js hands it in as `extArchived`), each with its extension.
const listOf = (m) => (m?.extArchived || []).find((a) => a?.count > 0) || null

/// The button at the bottom of a Mac's lists, or nothing.
export function archivedButton(home, m) {
  const a = listOf(m)
  if (!a) return ''
  const shown = visibleRows(home, m.key, a).length
  if (!shown) return ''
  return `<div class="ht-archived"><button class="go" data-act="archopen:${esc(m.key)}" title="The sessions handed off and archived on ${esc(m.name || 'this Mac')}: open, reactivate or delete them">Archived · ${shown}</button></div>`
}

const goneKey = (key, session) => key + '|' + session
const visibleRows = (home, key, a) => (a.rows || []).filter((r) => !home.archGone?.has(goneKey(key, r.session)))

/// The sheet, over Home, while open.
export function archivedSheet(home) {
  const key = home.archOpen
  if (!key) return ''
  const m = home.macOf(key), a = listOf(m)
  const rows = a ? visibleRows(home, key, a) : []
  const done = `<button class="go" data-act="archclose:">Done</button>`
  const clear = rows.length && a.clearAll ? `<button class="go danger" data-act="archall:${esc(key)}">${esc(rows.length === a.rows.length ? a.clearAll.title : `Clear all · ${rows.length} session${rows.length === 1 ? '' : 's'}`)}</button>` : ''
  const note = home.archNote ? `<div class="ht-notice warn"><div class="texts"><div class="t">Not done</div><div class="c">${esc(home.archNote)}</div></div></div>` : ''
  const body = note + (rows.length
    ? rows.map((r) => `<div class="arch-row" data-session="${esc(r.session)}"><div class="t">${esc(r.title)}</div>` +
      (r.lines || []).map((l) => `<div class="c">${esc(l)}</div>`).join('') +
      `<div class="arch-btns">${(r.actions || []).map((x, i) => `<button class="go${x.destructive ? ' danger' : ''}" data-act="archact:${esc(key)}|${esc(r.session)}|${i}">${esc(x.title)}</button>`).join('')}</div></div>`).join('')
    : `<div class="row quiet"><div class="texts"><div class="t">Nothing archived on ${esc(m?.name || 'this Mac')}.</div></div></div>`)
  const ask = home.archConfirm
    ? alert({ title: ask0(home.archConfirm), message: home.archConfirm.action.confirm, buttons: [{ act: 'archconfirm:no', label: 'Cancel', kind: 'cancel' }, { act: 'archconfirm:yes', label: home.archConfirm.action.title || 'OK', kind: home.archConfirm.action.destructive ? 'destructive' : 'default' }] })
    : ''
  return sheet({ title: `Archived on ${m?.name || 'this Mac'}`, leading: done, trailing: clear, body, width: 620, height: 640, cls: 'arch-sheet' }) + ask
}
const ask0 = (c) => (c.all ? 'Clear all' : c.action.title === 'Delete' ? 'Delete permanently' : c.action.title || 'Are you sure?')

/// Its taps (home.js hands each `arch*` act here): true when it was one of them.
export function archivedTap(home, kind, arg) {
  switch (kind) {
    case 'archopen': home.archOpen = arg; home.archNote = null; home.render(home.state); return true
    case 'archclose': home.archOpen = null; home.archConfirm = null; home.render(home.state); return true
    case 'archact': {
      const [key, session, i] = arg.split('|'), m = home.macOf(key), a = listOf(m)
      const r = a?.rows.find((x) => x.session === session), x = r?.actions?.[+i]
      if (!x) return true
      if (x.confirm) { home.archConfirm = { key, ext: a.ext, session, action: x }; home.render(home.state); return true }
      send(home, key, a.ext, session, x)
      return true
    }
    case 'archall': {
      const m = home.macOf(arg), a = listOf(m)
      if (!a?.clearAll) return true
      home.archConfirm = { key: arg, ext: a.ext, all: true, action: { ...a.clearAll, title: 'Clear all' } }
      home.render(home.state); return true
    }
    case 'archconfirm': {
      const c = home.archConfirm; home.archConfirm = null
      if (arg === 'yes' && c) {
        if (c.all) {
          const a = listOf(home.macOf(c.key))
          const sessions = (a?.rows || []).map((r) => r.session)
          send(home, c.key, c.ext, null, c.action, sessions)
        } else send(home, c.key, c.ext, c.session, c.action)
      }
      home.render(home.state); return true
    }
    default: return false
  }
}

/// The action to the Mac; Open closes the sheet; a Delete (or Clear all) hides its rows at once — back with the Mac's
/// why if it refused (Clear all: once done, any row the Mac still lists shows again).
function send(home, key, ext, session, x, all = null) {
  const deletes = x.id === 'codyArchiveDelete' || x.id === 'codyArchiveClearAll'
  const hidden = deletes ? (all || [session]).map((s) => goneKey(key, s)) : []
  home.archGone ||= new Set()
  for (const g of hidden) home.archGone.add(g)
  home.archNote = null
  if (x.id === 'open') home.archOpen = null
  const r = home.a.extAction?.(key, ext, { action: x.id, args: x.args || {}, target: 'home' })
  Promise.resolve(r).then((res) => {
    if (res && res.ok === false) {
      for (const g of hidden) home.archGone.delete(g)
      home.archNote = String(res.error || 'The Mac couldn’t do that.')
      home.render(home.state)
    } else if (all) {
      // Clear all done: what the Mac deleted is gone from its list; any it couldn't (a claude still stopping) shows again
      for (const g of hidden) home.archGone.delete(g)
      home.render(home.state)
    }
  }, (e) => {
    for (const g of hidden) home.archGone.delete(g)
    home.archNote = String(e?.message || e)
    home.render(home.state)
  })
  home.render(home.state)
}
